import React, { FC, useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router';
import HistoryOrdersList from './Components/HistoryOrdersList';
import styled from '@emotion/styled';
import useI18n from '~Translations';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import HistoryOrder from './Components/HistoryOrder';
import OngoingCheque from './Components/OngoingCheque';
import { Box } from '~Styles';
import BongMaster from '~Scenes/Tables/Components/BongMaster';

const PageWrapper = styled.div`
  background-color: var(--colorBlueGray1200);
  height: calc(100vh - 64px);
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
`;

const Wrapper = styled(Box)`
  flex: 1;
  overflow: hidden;
  padding: 20px 40px 0px 20px;
`;

const ListWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  box-sizing: border-box;
  padding-bottom: 40px;
`;

const ContentWrapper = styled.div`
  /**
   * The detail view (HistoryOrder) caps at 600px, so cap the wrapper too.
   * A flex item stops growing at its max-width, letting ListWrapper claim
   * the remaining space instead of reserving an unused 50%.
   */
  flex: 1;
  max-width: 600px;
  /**
   * Without its own scroll container, tall HistoryOrder content gets clipped
   * by PageWrapper's fixed height + overflow: hidden with no way to reach it.
   */
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
`;

const History: FC = () => {
  const { i18n } = useI18n();
  const location = useLocation();
  const history = useHistory();

  // Extract the selected order ID from URL params
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedOrderId = searchParams.get('orderId');
  const selectedChequeId = searchParams.get('chequeId');

  // Read the current search from history at call time so these callbacks can stay
  // referentially stable (no location.search dependency), keeping the memoized rows from re-rendering
  const onOrderClick = useCallback(
    (orderId: string) => {
      const params = new URLSearchParams(history.location.search);
      params.set('orderId', orderId);
      params.delete('chequeId');
      // Use replace instead of push to update URL without adding to browser history
      history.replace({
        search: params.toString(),
      });
    },
    [history],
  );

  const onChequeClick = useCallback(
    (chequeId: string) => {
      const params = new URLSearchParams(history.location.search);
      params.set('chequeId', chequeId);
      params.delete('orderId');
      // Use replace instead of push to update URL without adding to browser history
      history.replace({
        search: params.toString(),
      });
    },
    [history],
  );

  return (
    <div>
      <PageWrapper>
        <BackTopBar title={i18n('History.Title')} dark></BackTopBar>
        <Wrapper display="flex" gap="large" fluid>
          <ListWrapper>
            <HistoryOrdersList onOrderClick={onOrderClick} onChequeClick={onChequeClick} />
          </ListWrapper>

          <ContentWrapper>
            {selectedOrderId && <HistoryOrder orderId={selectedOrderId} />}

            {selectedChequeId && <OngoingCheque chequeId={selectedChequeId} />}
          </ContentWrapper>
        </Wrapper>
      </PageWrapper>
      <BongMaster />
    </div>
  );
};

export default History;
